import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { comicDetails } from "@/mocks/comicDetail";
import { hotRanking } from "@/mocks/comics";

export default function ComicDetailPage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const comic = slug ? comicDetails[slug] : undefined;

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-semibold text-foreground-800 mb-2">漫画未找到</h2>
            <p className="text-foreground-500 mb-6">该漫画不存在或已被下架</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i> 返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedComics = hotRanking.filter((c) => c.slug !== slug).slice(0, 5);

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-8">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6 flex-wrap">
            <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <Link
              to={`/category/${comic.genre[0] === "恋爱漫画" ? "romance" : comic.genre[0] === "热血动作" ? "action" : "fantasy"}`}
              className="hover:text-primary-500 transition-colors whitespace-nowrap"
            >
              {comic.genre[0]}
            </Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-800 font-medium line-clamp-1">{comic.title}</span>
          </nav>

          <div className="flex flex-col md:flex-row gap-8">
            <div className="flex-shrink-0 w-full md:w-72">
              <div className="aspect-[3/4] rounded-xl overflow-hidden">
                <img
                  src={comic.cover}
                  alt={comic.title}
                  className="w-full h-full object-cover"
                  title={`${comic.title} - 无删减韩漫大全`}
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1
                className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                {comic.title}
              </h1>

              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="flex items-center gap-1 text-sm text-accent-600 font-semibold">
                  <i className="ri-star-fill text-accent-500"></i> {comic.rating}
                </span>
                <span className="text-sm text-foreground-400">|</span>
                <span className="text-sm text-foreground-600">
                  <i className="ri-eye-line mr-1"></i>{comic.views}
                </span>
                <span className="text-sm text-foreground-400">|</span>
                <span className="text-sm text-foreground-600">
                  <i className="ri-user-line mr-1"></i>{comic.followers} 收藏
                </span>
                <span className="text-sm text-foreground-400">|</span>
                <span
                  className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                    comic.status === "已完结"
                      ? "bg-green-100 text-green-700"
                      : "bg-accent-100 text-accent-700"
                  }`}
                >
                  {comic.status}
                </span>
              </div>

              <div className="flex flex-wrap gap-2 mb-4">
                <span className="text-sm text-foreground-500">作者：</span>
                <span className="text-sm text-foreground-800">{comic.author}</span>
                <span className="text-sm text-foreground-400">/</span>
                <span className="text-sm text-foreground-800">{comic.artist}</span>
              </div>

              <div className="flex flex-wrap gap-2 mb-4">
                {comic.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2.5 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700 whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <div className="flex flex-wrap gap-2 mb-6">
                {comic.genre.map((g) => (
                  <Link
                    key={g}
                    to={`/category/${g === "恋爱漫画" ? "romance" : g === "热血动作" ? "action" : g === "奇幻冒险" ? "fantasy" : g === "都市剧情" ? "drama" : g === "悬疑惊悚" ? "thriller" : g === "古风历史" ? "historical" : "romance"}`}
                    className="px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors whitespace-nowrap"
                  >
                    {g}
                  </Link>
                ))}
              </div>

              <p className="text-sm text-foreground-600 leading-relaxed mb-6 max-w-2xl">
                {comic.description}
              </p>

              <div className="flex flex-wrap gap-3">
                <Link
                  to={`/comic/${comic.slug}/chapter/${comic.chapters[0]?.id || 1}`}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-full text-sm transition-colors whitespace-nowrap"
                >
                  <i className="ri-play-line"></i> 开始阅读
                </Link>
                <button className="inline-flex items-center gap-2 px-6 py-3 bg-secondary-100 hover:bg-secondary-200 text-secondary-800 font-medium rounded-full text-sm transition-colors whitespace-nowrap">
                  <i className="ri-bookmark-line"></i> 加入收藏
                </button>
              </div>
            </div>
          </div>

          <section className="mt-12">
            <h2
              className="text-xl font-bold text-foreground-950 mb-6"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              章节列表
              <span className="text-sm font-normal text-foreground-500 ml-2">
                共 {comic.totalChapters} 话 · 更新于 {comic.lastUpdate}
              </span>
            </h2>

            <div className="border border-background-200/70 rounded-xl overflow-hidden">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                {comic.chapters.map((ch) => (
                  <Link
                    key={ch.id}
                    to={`/comic/${comic.slug}/chapter/${ch.id}`}
                    className="flex items-center justify-between px-5 py-3.5 border-b border-r border-background-200/50 hover:bg-background-100 transition-colors group"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-sm font-medium text-foreground-800 group-hover:text-primary-500 transition-colors line-clamp-1">
                        {ch.title}
                      </span>
                      {ch.isFree && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-700 whitespace-nowrap">
                          免费
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-foreground-400 flex-shrink-0 ml-2">{ch.date}</span>
                  </Link>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-center mt-6 gap-2">
              <button className="w-9 h-9 rounded-lg bg-background-100 text-foreground-500 flex items-center justify-center text-sm hover:bg-background-200 transition-colors">
                <i className="ri-arrow-left-s-line"></i>
              </button>
              <button className="w-9 h-9 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center text-sm font-medium">
                1
              </button>
              <button className="w-9 h-9 rounded-lg bg-background-100 text-foreground-600 flex items-center justify-center text-sm hover:bg-background-200 transition-colors">
                2
              </button>
              <button className="w-9 h-9 rounded-lg bg-background-100 text-foreground-600 flex items-center justify-center text-sm hover:bg-background-200 transition-colors">
                3
              </button>
              <span className="text-foreground-400 text-sm">...</span>
              <button className="w-9 h-9 rounded-lg bg-background-100 text-foreground-600 flex items-center justify-center text-sm hover:bg-background-200 transition-colors">
                {Math.ceil(comic.totalChapters / 20)}
              </button>
              <button className="w-9 h-9 rounded-lg bg-background-100 text-foreground-500 flex items-center justify-center text-sm hover:bg-background-200 transition-colors">
                <i className="ri-arrow-right-s-line"></i>
              </button>
            </div>
          </section>

          <section className="mt-14">
            <h2
              className="text-xl font-bold text-foreground-950 mb-6"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              猜你喜欢
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
              {relatedComics.map((c) => (
                <Link key={c.rank} to={`/comic/${c.slug}`} className="group">
                  <div className="aspect-[3/4] rounded-xl overflow-hidden mb-3">
                    <img
                      src={c.cover}
                      alt={c.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <h4 className="text-sm font-semibold text-foreground-900 line-clamp-1 group-hover:text-primary-500 transition-colors">
                    {c.title}
                  </h4>
                  <span className="text-xs text-foreground-500">{c.genre}</span>
                </Link>
              ))}
            </div>
          </section>
        </div>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ComicSeries",
            name: comic.title,
            author: { "@type": "Person", name: comic.author },
            description: comic.description,
            genre: comic.genre,
            inLanguage: "zh-CN",
            dateModified: comic.lastUpdate,
          }),
        }}
      />
    </>
  );
}